import { getActivePinia } from 'pinia'
import { Auth } from 'aws-amplify'
import type { AxiosInstance } from 'axios'
import type { Actions, AppInfo, Getters, SignOutParams, State } from '../../../composables/useGlobal/types'
import type { NcBreakpoint } from '~/lib/constants'

export interface ActionsEE {
  getMainUrl: () => string | undefined
  checkForCognitoToken: (params?: { skipRedirect?: boolean }) => Promise<void>
}

export function useGlobalActions(state: State, getters: Getters): Actions & ActionsEE {
  const isTokenUpdatedTab = useState('isTokenUpdatedTab', () => false)

  const setIsMobileMode = (isMobileMode: boolean) => {
    state.isMobileMode.value = isMobileMode
  }

  const setActiveBreakpoint = (breakpoint: NcBreakpoint) => {
    state.activeBreakpoint.value = breakpoint
  }

  // todo: move to pinia/global state
  const isAmplifyConfigured = useState('is-amplify-configured', () => false)

  /** Sign out by deleting the token from localStorage */
  const signOut: Actions['signOut'] = async ({
    redirectToSignin,
    signinUrl = '/signin',
    skipRedirect = true,
    skipApiCall = false,
  }: SignOutParams = {}) => {
    let signoutRes
    const nuxtApp = useNuxtApp()
    try {
      // call and invalidate refresh token only if user manually triggered logout
      if (!skipApiCall) {
        // invalidate token and refresh token on server
        signoutRes = await nuxtApp.$api.auth.signout()
      }
    } catch {
      // ignore error
    } finally {
      // clear token and user data
      state.token.value = null
      state.user.value = null

      window.$chatwoot?.reset?.()
    }

    // clear amplify session if configured
    try {
      if (isAmplifyConfigured.value) await Auth.signOut()
    } catch {
      // ignore error
    }

    // Drop any leftover 2FA challenge state. `useTwoFactorSignin` is a
    // shared composable (VueUse singleton), not a Pinia store, so the
    // pn._s loop below doesn't touch it. Without this, a logout right
    // after a successful TOTP verify leaves `twoFactorRequired = true`
    // in the singleton, and the next render of /signin shows the TOTP
    // form instead of the email/password form.
    try {
      await nuxtApp.runWithContext(() => {
        useTwoFactorSignin().reset()
      })
    } catch {
      // composable may not have been instantiated yet — safe to ignore
    }

    // todo: update type in swagger.json
    if (!skipRedirect && (signoutRes as any)?.redirect_url) {
      location.href = (signoutRes as any).redirect_url
    }

    if (redirectToSignin) {
      await navigateTo(signinUrl)
    }

    // clear all stores data on logout
    const pn = getActivePinia()
    if (pn) {
      pn._s.forEach((store) => {
        store.$dispose()
        delete pn.state.value[store.$id]
      })
    }
  }

  /** Sign in by setting the token in localStorage
   * keepProps - is for keeping any existing role info if user id is same as previous user
   */
  const signIn: Actions['signIn'] = (newToken, keepProps = false) => {
    isTokenUpdatedTab.value = true
    state.token.value = newToken

    if (state.jwtPayload.value) {
      state.user.value = {
        id: state.jwtPayload.value.id,
        ...(keepProps && state.user.value?.id === state.jwtPayload.value.id ? state.user.value || {} : {}),
        email: state.jwtPayload.value.email,
        firstname: state.jwtPayload.value.firstname,
        lastname: state.jwtPayload.value.lastname,
        roles: state.jwtPayload.value.roles,
      }
    }
  }

  async function detectLoginMethod() {
    try {
      const session = await Auth.currentSession()
      const decodedidToken = session.getIdToken().decodePayload()

      if (decodedidToken?.identities?.length) {
        // Federated provider (Google, Facebook, etc.)
        return decodedidToken.identities[0].providerName?.toLowerCase?.() // e.g. "Google"
      }

      // If no identities, usually means Cognito native (email/password)
      return 'email'
    } catch (err) {
      console.log(err)
      return null
    }
  }

  const checkForCognitoToken = async ({
    axiosInstance,
  }: {
    axiosInstance?: any
    skipSignOut?: boolean
  } = {}) => {
    if (state.token.value && getters.signedIn.value) {
      return
    }

    try {
      const cognitoUserSession = await Auth.currentSession()
      const idToken = cognitoUserSession.getIdToken()
      const jwt = idToken.getJwtToken()

      // extract auth method
      detectLoginMethod(idToken).then((method) => {
        if (method) state.lastUsedAuthMethod.value = method
      })

      const nuxtApp = useNuxtApp()

      if (!axiosInstance) {
        axiosInstance = nuxtApp.$api?.instance
      }

      // Capture the URL the user was on when Cognito kicked in, so the
      // BE can echo it back inside the twoFactorToken and we can re-land
      // the user there after `/api/v2/auth/mfa/verify`. Skipped for the
      // /signin path itself to avoid trivial loops.
      const here = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/'
      const redirect = here && !here.startsWith('/signin') && !here.startsWith('/sso') ? here : undefined

      const tokenRes = await axiosInstance.post(
        '/auth/cognito',
        { ...(redirect ? { redirect } : {}) },
        {
          headers: {
            'xc-cognito': jwt,
          },
        },
      )
      // NocoDB-side TOTP layered on top of Cognito (Cloud-only). The BE
      // returns `{ twoFactorRequired, twoFactorToken }` when the user
      // has TOTP enrolled. Stage the token through
      // `useTwoFactorSignin().stagePendingToken` so the reactive state
      // flips immediately — Signin.vue may already be mounted (and its
      // one-shot onBeforeMount hydration has already run), in which
      // case writing sessionStorage alone would leave it stuck on the
      // email/password form because `navigateTo('/signin')` from
      // /signin is a no-op (no remount). sessionStorage write still
      // happens inside stagePendingToken to cover hard reloads.
      if (tokenRes.data.twoFactorRequired && tokenRes.data.twoFactorToken) {
        await nuxtApp.runWithContext(() => {
          useTwoFactorSignin().stagePendingToken(tokenRes.data.twoFactorToken)
          return navigateTo('/signin')
        })
        return
      }
      if (tokenRes.data.token) {
        updateFirstTimeUser()
        const token = tokenRes.data.token
        await signIn(token)
        return token
      }
    } catch (err) {}
  }

  /** manually try to refresh token */
  const _refreshToken = async ({
    axiosInstance,
    cognitoOnly = false,
  }: {
    axiosInstance?: AxiosInstance
    skipSignOut?: boolean
    cognitoOnly?: boolean
  } = {}) => {
    const nuxtApp = useNuxtApp()
    const t = nuxtApp.vueApp.i18n.global.t

    if (!axiosInstance) {
      axiosInstance = nuxtApp.$api?.instance
    }

    try {
      if (cognitoOnly) {
        return await checkForCognitoToken({
          axiosInstance,
        })
      }

      const response = await axiosInstance.post('/auth/token/refresh', null, {
        withCredentials: true,
      })

      if (response.data?.token) {
        signIn(response.data.token, true)
        return response.data.token
      }
    } catch (e) {
      // if error occurs, check cognito and generate token or signout
      if (isAmplifyConfigured.value) {
        // reset token value to null since cognito token is not valid
        state.token.value = null
        return await checkForCognitoToken({
          axiosInstance,
        })
      } else if (state.token.value && state.user.value) {
        await signOut({
          skipApiCall: true,
        })
        message.error(t('msg.error.youHaveBeenSignedOut'))
      }
    }
  }

  const refreshToken = useSharedExecutionFn('refreshToken', _refreshToken, {
    timeout: 10000,
    storageDelay: 1000,
  })

  const loadAppInfo = async () => {
    try {
      state.appInfoStatus.value = 'loading'
      const nuxtApp = useNuxtApp()
      state.appInfo.value = (await nuxtApp.$api.utils.appInfo()) as AppInfo
      state.appInfoStatus.value = 'loaded'
    } catch (e) {
      state.appInfoStatus.value = 'error'
      console.error(e)
    }
  }

  // todo: remove and use navigateToProject
  const navigateToProject = ({
    workspaceId: _workspaceId,
    baseId,
    query,
  }: {
    workspaceId?: string
    baseId?: string
    query?: any
  }) => {
    const queryParams = query ? `?${new URLSearchParams(query).toString()}` : ''
    const workspaceId = _workspaceId || 'app'
    let path: string
    if (baseId) {
      path = `/${workspaceId}/${baseId}${queryParams}`
    } else {
      path = _workspaceId ? `/${workspaceId}${queryParams}` : `/${queryParams}`
    }

    // if (state.appInfo.value.baseHostName && location.hostname !== `${workspaceId}.${state.appInfo.value.baseHostName}`) {
    //   location.href = `https://${workspaceId}.${state.appInfo.value.baseHostName}${state.appInfo.value.dashboardPath}#${path}`
    // } else {
    navigateTo(path)
    // }
  }

  const ncNavigateTo = ({
    workspaceId: _workspaceId,
    baseId,
    tableId,
    tableTitle,
    viewId,
    viewTitle,
    query,
    scriptId,
    scriptTitle,
    workflowId,
    workflowTitle,
    replace,
    dashboardId,
    dashboardTitle,
    docId,
    docTitle,
    newTab,
  }: {
    workspaceId?: string
    baseId?: string
    tableId?: string
    tableTitle?: string
    viewId?: string
    viewTitle?: string
    scriptId?: string
    scriptTitle?: string
    dashboardId?: string
    dashboardTitle?: string
    docId?: string
    docTitle?: string
    workflowId?: string
    workflowTitle?: string
    query?: string
    replace?: boolean
    newTab?: boolean
  }) => {
    const tablePath = tableId
      ? `/${tableId}${
          viewId
            ? `/${viewId}${toReadableUrlSlug([tableTitle, viewTitle]) ? `/${toReadableUrlSlug([tableTitle, viewTitle])}` : ''}`
            : ''
        }`
      : ''

    const scriptPath = scriptId
      ? `/scripts/${scriptId}${toReadableUrlSlug([scriptTitle]) ? `/${toReadableUrlSlug([scriptTitle])}` : ''}`
      : ''

    const dashboardPath = dashboardId
      ? `/dashboards/${dashboardId}${toReadableUrlSlug([dashboardTitle]) ? `/${toReadableUrlSlug([dashboardTitle])}` : ''}`
      : ''

    const workflowPath = workflowId
      ? `/workflows/${workflowId}${toReadableUrlSlug([workflowTitle]) ? `/${toReadableUrlSlug([workflowTitle])}` : ''}`
      : ''

    const docPath = docId ? `/docs/${docId}${toReadableUrlSlug([docTitle]) ? `/${toReadableUrlSlug([docTitle])}` : ''}` : ''

    const queryParams = query ? `?${new URLSearchParams(query).toString()}` : ''
    const workspaceId = _workspaceId || 'app'

    let path: string
    if (baseId) {
      if (scriptId) {
        path = `/${workspaceId}/${baseId}${scriptPath}${queryParams}`
      } else if (dashboardId) {
        path = `/${workspaceId}/${baseId}${dashboardPath}${queryParams}`
      } else if (workflowId) {
        path = `/${workspaceId}/${baseId}${workflowPath}${queryParams}`
      } else if (docId) {
        path = `/${workspaceId}/${baseId}${docPath}${queryParams}`
      } else {
        path = `/${workspaceId}/${baseId}${tablePath}${queryParams}`
      }
    } else {
      path = _workspaceId ? `/${workspaceId}${queryParams}` : `/${queryParams}`
    }

    if (newTab) {
      window.open(`${window.location.origin}#${path}`, '_blank')
    } else {
      // if (state.appInfo.value.baseHostName && location.hostname !== `${workspaceId}.${state.appInfo.value.baseHostName}`) {
      //   location.href = `https://${workspaceId}.${state.appInfo.value.baseHostName}${state.appInfo.value.dashboardPath}#${path}`
      // } else {
      navigateTo(path, { replace })
      // }
    }
  }

  const getBaseUrl = (workspaceId: string) => {
    if (
      !['base', 'nc', 'view'].includes(workspaceId) &&
      state.appInfo.value.baseHostName &&
      location.hostname !== `${workspaceId}.${state.appInfo.value.baseHostName}`
    ) {
      return `https://${workspaceId}.${state.appInfo.value.baseHostName}`
    }
    return undefined
  }

  // get the base url of the app id base subdomain is used
  // eg: https://app.nocodb.com
  const getMainUrl = () => {
    if (state.appInfo.value.mainSubDomain && state.appInfo.value.baseHostName) {
      return `https://${state.appInfo.value.mainSubDomain}.${state.appInfo.value.baseHostName}`
    }
    return undefined
  }

  const setGridViewPageSize = (pageSize: number) => {
    state.gridViewPageSize.value = pageSize
  }

  const setLeftSidebarSize = ({ old, current }: { old?: number; current?: number }) => {
    state.leftSidebarSize.value = {
      old: old ?? state.leftSidebarSize.value.old,
      current: current ?? state.leftSidebarSize.value.current,
    }
  }

  const setAddNewRecordGridMode = (isGridMode: boolean) => {
    state.isAddNewRecordGridMode.value = isGridMode
  }

  const updateSyncDataUpvotes = (upvotes: string[]) => {
    state.syncDataUpvotes.value = upvotes
  }

  return {
    signIn,
    signOut,
    refreshToken,
    loadAppInfo,
    setIsMobileMode,
    setActiveBreakpoint,
    navigateToProject,
    getBaseUrl,
    ncNavigateTo,
    getMainUrl,
    setGridViewPageSize,
    setLeftSidebarSize,
    setAddNewRecordGridMode,
    updateSyncDataUpvotes,
  }
}
